import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  InviteMembersResult,
  MemberInvitationCandidate,
  MemberInvitationPage,
} from '@up2d8/contracts';
import { formatDate } from '../../shared/format';
import { useSession } from '../../shared/session';
import { useProblemText } from '../../shared/use-problem-text';
import {
  Badge,
  Btn,
  Card,
  CardFoot,
  CardHead,
  CardPad,
  Checkbox,
  Modal,
  Note,
  Percent,
  Segmented,
  Stack,
  Table,
} from '../admin-kit';
import { chunks, memberMailState } from './member-invitations';

type Filter = 'not_invited' | 'invited' | 'all';

/**
 * "Nog niet uitgenodigd (N)": leden die in de app staan maar nog nooit toegang hadden, meestal na
 * de overstap van het oude platform (ADR-0101).
 *
 * Alleen voor wie accounts beheert, en alleen als er iemand in staat. Uitnodigen gaat in pakjes van
 * 25, zodat de voortgang zichtbaar blijft en een fout halverwege niet alles ongedaan laat lijken; wat
 * verstuurd is, blijft verstuurd. De woorden volgen het type organisatie (ORG-2): "medewerkers"
 * worden "leden" bij een club.
 */
export function MemberInvitationsCard() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const text = useProblemText();
  const queryClient = useQueryClient();
  const allowed = has('identity.account.manage');

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>('not_invited');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [confirmAll, setConfirmAll] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<{
    invited: number;
    failed: number;
    skipped: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useInfiniteQuery({
    queryKey: ['identity', 'member-invitations', filter],
    enabled: allowed,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: MemberInvitationPage) => last.nextCursor ?? undefined,
    queryFn: async ({ pageParam }): Promise<MemberInvitationPage> => {
      const { data, error: problem } = await api.GET('/api/v1/accounts/member-invitations', {
        params: { query: { state: filter, limit: 50, cursor: pageParam } },
      });
      if (problem || !data) throw problem ?? new Error('Lijst niet geladen');
      return data as MemberInvitationPage;
    },
  });

  const counts = list.data?.pages[0]?.counts;
  if (!allowed || !counts || counts.notInvited + counts.invited === 0) return null;

  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const busy = progress !== null;
  const allOnPage = items.length > 0 && items.every((item) => checked.has(item.accountId));

  const send = async (body: { accountIds: string[] } | { all: true }) => {
    const { data, error: problem } = await api.POST('/api/v1/accounts/member-invitations', {
      body: body as never,
    });
    if (problem || !data) throw problem ?? new Error('Uitnodigen mislukt');
    return data as InviteMembersResult;
  };

  /** Pakje na pakje; stopt bij een fout en toont wat al gelukt is. */
  const run = async (mode: { ids: string[] } | { all: number }) => {
    const total = 'ids' in mode ? mode.ids.length : mode.all;
    const tally = { invited: 0, failed: 0, skipped: 0 };
    setError(null);
    setResult(null);
    setProgress({ done: 0, total });
    try {
      if ('ids' in mode) {
        let done = 0;
        for (const part of chunks(mode.ids)) {
          const answer = await send({ accountIds: part });
          tally.invited += answer.invited;
          tally.failed += answer.mailFailed;
          tally.skipped += answer.skipped.length;
          done += part.length;
          setProgress({ done, total });
        }
      } else {
        // "Alle": telkens de volgende die nog nooit uitgenodigd werden, tot er niemand meer is. Een
        // pakje zonder resultaat stopt de lus: anders draait een overgeslagen rest eindeloos rond.
        for (;;) {
          const answer = await send({ all: true });
          tally.invited += answer.invited;
          tally.failed += answer.mailFailed;
          tally.skipped += answer.skipped.length;
          setProgress({ done: Math.min(total, tally.invited + tally.skipped), total });
          if (answer.remaining === 0 || answer.invited === 0) break;
        }
      }
    } catch (problem) {
      setError(text(problem, t('memberInvites.rateLimited')));
    } finally {
      setProgress(null);
      setResult(tally);
      setChecked(new Set());
      void queryClient.invalidateQueries({ queryKey: ['identity', 'member-invitations'] });
    }
  };

  const toggle = (accountId: string, on: boolean) =>
    setChecked((current) => {
      const next = new Set(current);
      if (on) next.add(accountId);
      else next.delete(accountId);
      return next;
    });

  const status = (item: MemberInvitationCandidate) => {
    const state = memberMailState(item);
    switch (state) {
      case 'not_invited':
        return <Badge>{t('memberInvites.statusNotInvited')}</Badge>;
      case 'failed':
        return <Badge tone="danger">{t('memberInvites.statusFailed')}</Badge>;
      case 'pending':
        return <Badge tone="attention">{t('memberInvites.statusPending')}</Badge>;
      case 'expired':
        return <Badge tone="attention">{t('memberInvites.statusExpired')}</Badge>;
      default:
        return (
          <Badge tone="ok">
            {t('memberInvites.statusSent', { date: formatDate(item.lastSentAt) })}
          </Badge>
        );
    }
  };

  return (
    <Card testId="member-invitations">
      <CardHead
        title={t('memberInvites.title')}
        count={counts.notInvited}
        countAttention={counts.notInvited > 0}
        actions={
          <Btn
            variant="ghost"
            testId="member-invitations-toggle"
            onClick={() => setOpen((value) => !value)}
          >
            {open ? t('memberInvites.hide') : t('memberInvites.show')}
          </Btn>
        }
      />
      <CardPad>
        <p className="a-muted">{t('memberInvites.intro')}</p>
        <p className="a-small" data-testid="member-invitations-summary">
          {t('memberInvites.summary', counts)}
        </p>
        {progress ? (
          <div role="status" aria-live="polite" data-testid="member-invitations-progress">
            <Percent
              value={progress.total ? (progress.done / progress.total) * 100 : 0}
              label={t('memberInvites.progress', progress)}
            />
          </div>
        ) : null}
        {result && !busy ? (
          <Note tone={result.failed > 0 ? 'attention' : 'info'} role="status">
            <p>{t('memberInvites.done', result)}</p>
            {result.failed > 0 ? <p>{t('memberInvites.failedHint')}</p> : null}
          </Note>
        ) : null}
        {error ? (
          <Note tone="danger" role="alert">
            {error}
          </Note>
        ) : null}
      </CardPad>

      {open ? (
        <>
          <CardPad>
            <Stack>
              <Segmented<Filter>
                label={t('memberInvites.title')}
                value={filter}
                disabled={busy}
                onChange={(value) => {
                  setFilter(value);
                  setChecked(new Set());
                }}
                options={[
                  { value: 'not_invited', label: t('memberInvites.filterNotInvited') },
                  { value: 'invited', label: t('memberInvites.filterInvited') },
                  { value: 'all', label: t('memberInvites.filterAll') },
                ]}
              />
              <div className="a-actions">
                <Btn
                  testId="member-invitations-invite-selected"
                  disabled={busy || checked.size === 0}
                  onClick={() => void run({ ids: [...checked] })}
                >
                  {t('memberInvites.inviteSelected', { count: checked.size })}
                </Btn>
                {counts.notInvited > 0 ? (
                  <Btn
                    variant="sec"
                    testId="member-invitations-invite-all"
                    disabled={busy}
                    onClick={() => setConfirmAll(true)}
                  >
                    {t('memberInvites.inviteAll', { count: counts.notInvited })}
                  </Btn>
                ) : null}
              </div>
            </Stack>
          </CardPad>
          {items.length === 0 ? (
            <CardPad>
              <p className="a-muted">{t('memberInvites.empty')}</p>
            </CardPad>
          ) : (
            <Table label={t('memberInvites.title')} testId="member-invitations-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <Checkbox
                      checked={allOnPage}
                      disabled={busy}
                      label={<span className="sr-only">{t('memberInvites.selectAll')}</span>}
                      testId="member-invitations-select-all"
                      onChange={(on) =>
                        setChecked(on ? new Set(items.map((item) => item.accountId)) : new Set())
                      }
                    />
                  </th>
                  <th>{t('memberInvites.colName')}</th>
                  <th>{t('memberInvites.colEmail')}</th>
                  <th>{t('memberInvites.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.accountId} className={checked.has(item.accountId) ? 'sel' : ''}>
                    <td>
                      <Checkbox
                        checked={checked.has(item.accountId)}
                        disabled={busy}
                        label={
                          <span className="sr-only">
                            {t('memberInvites.selectOne', { name: item.displayName })}
                          </span>
                        }
                        onChange={(on) => toggle(item.accountId, on)}
                      />
                    </td>
                    <td className="a-name">{item.displayName}</td>
                    <td className="a-muted">{item.email}</td>
                    <td>
                      {status(item)}
                      {item.resendAllowedAt ? (
                        <span className="a-hint" style={{ display: 'block' }}>
                          {t('memberInvites.resendFrom', {
                            date: formatDate(item.resendAllowedAt, {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            }),
                          })}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {list.hasNextPage ? (
            <CardFoot>
              <span />
              <Btn
                variant="sec"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                {t('memberInvites.more')}
              </Btn>
            </CardFoot>
          ) : null}
        </>
      ) : null}

      <Modal
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        title={t('memberInvites.inviteAll', { count: counts.notInvited })}
        testId="member-invitations-confirm"
        footer={
          <>
            <Btn variant="sec" onClick={() => setConfirmAll(false)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              testId="member-invitations-confirm-all"
              onClick={() => {
                setConfirmAll(false);
                void run({ all: counts.notInvited });
              }}
            >
              {t('memberInvites.inviteAll', { count: counts.notInvited })}
            </Btn>
          </>
        }
      >
        <p>{t('memberInvites.confirmAll', { count: counts.notInvited })}</p>
      </Modal>
    </Card>
  );
}
